import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, ArrowLeft, Eye, Upload, X, CheckCircle, Plus, Crop as CropIcon, Image as ImageIcon, Loader2, ImagePlus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { BlogPost, getBlogPostById, createBlogPost, updateBlogPost } from '../lib/blog';
import MDEditor, { ICommand, commands } from '@uiw/react-md-editor';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// Helper function to create aspect ratio crop
function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

// Helper function to get cropped image blob
function getCroppedImg(image: HTMLImageElement, crop: PixelCrop): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Calculate the scale between the displayed image and natural image
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Convert crop coordinates to natural image coordinates
  const sourceX = crop.x * scaleX;
  const sourceY = crop.y * scaleY;
  const sourceWidth = crop.width * scaleX;
  const sourceHeight = crop.height * scaleY;

  // Target high-quality output dimensions (16:9 ratio)
  const targetWidth = 1200;
  const targetHeight = 675;

  // Calculate output dimensions - maintain aspect ratio
  const cropAspectRatio = sourceWidth / sourceHeight;
  let outputWidth, outputHeight;

  if (sourceWidth >= targetWidth && sourceHeight >= targetHeight) {
    // Source is large enough, use target dimensions
    outputWidth = targetWidth;
    outputHeight = targetHeight;
  } else {
    // Scale up proportionally but maintain minimum quality
    const minWidth = 800;
    const minHeight = 450;

    if (sourceWidth < minWidth || sourceHeight < minHeight) {
      // Scale to minimum dimensions
      outputWidth = minWidth;
      outputHeight = minHeight;
    } else {
      // Use source dimensions
      outputWidth = sourceWidth;
      outputHeight = sourceHeight;
    }
  }

  canvas.width = outputWidth;
  canvas.height = outputHeight;

  // Enable high-quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw the cropped portion of the image
  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

export function BlogEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState<Partial<BlogPost & { category_id: string | null }>>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    status: 'draft',
    meta_title: '',
    meta_description: '',
    og_title: '',
    og_description: '',
    og_image: '',
    featured_image: null,
    category_id: null,
    tags: []
  });
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [tags, setTags] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);
  
  // Crop-related state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [processing, setProcessing] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingContentImage, setUploadingContentImage] = useState(false);
  const [imageUploadSuccess, setImageUploadSuccess] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const imageUploadInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (id) {
      loadPost(id);
    }
    loadCategoriesAndTags();
  }, [id]);

  async function checkAdminStatus() {
    if (!user) {
      navigate('/dashboard');
      return;
    }

    const { data, error } = await supabase
      .from('users')
      .select('system_role')
      .eq('id', user.id)
      .single();

    if (error || !data?.system_role || !['super_admin', 'content_editor'].includes(data.system_role)) {
      navigate('/dashboard');
      return;
    }

    setIsAdmin(true);
  }

  async function loadPost(postId: string) {
    setLoading(true);
    try {
      const post = await getBlogPostById(postId);
      if (post) {
        setPost(post);
        setSelectedTags(post.tags?.map(t => t.id) || []);
        if (post.featured_image) {
          setPreviewImageUrl(post.featured_image);
        }
      } else {
        navigate('/dashboard/blog');
      }
    } catch (error) {
      console.error('Error loading post:', error);
      navigate('/dashboard/blog');
    } finally {
      setLoading(false);
    }
  }

  async function loadCategoriesAndTags() {
    try {
      const [categoriesData, tagsData] = await Promise.all([
        supabase.from('blog_categories' as any).select('id, name').order('name'),
        supabase.from('blog_tags' as any).select('id, name').order('name')
      ]);

      if (categoriesData.data) setCategories(categoriesData.data as unknown as { id: string; name: string; }[]);
      if (tagsData.data) setTags(tagsData.data as unknown as { id: string; name: string; }[]);
    } catch (error) {
      console.error('Error loading categories and tags:', error);
    }
  }

  async function createCategory() {
    if (!newCategoryName.trim()) return;
    
    setIsAddingCategory(true);
    try {
      const slug = newCategoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      
      const { data, error } = await supabase
        .from('blog_categories' as any)
        .insert({
          name: newCategoryName.trim(),
          slug: slug,
          description: null
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Add to categories list and select it
      setCategories([...categories, data as unknown as { id: string; name: string; }]);
      setPost(prev => ({ ...prev, category_id: (data as any).id }));
      setNewCategoryName('');
      setShowNewCategory(false);
    } catch (error: any) {
      console.error('Error creating category:', error);
      if (error.code === '23505') {
        alert('A category with this name already exists.');
      } else {
        alert('Failed to create category.');
      }
    } finally {
      setIsAddingCategory(false);
    }
  }

  async function createTag() {
    if (!newTagName.trim()) return;
    
    setIsAddingTag(true);
    try {
      const slug = newTagName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      
      const { data, error } = await supabase
        .from('blog_tags' as any)
        .insert({
          name: newTagName.trim(),
          slug: slug
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Add to tags list and select it
      setTags([...tags, data as unknown as { id: string; name: string; }]);
      setSelectedTags([...selectedTags, (data as any).id]);
      setNewTagName('');
      setShowNewTag(false);
    } catch (error: any) {
      console.error('Error creating tag:', error);
      if (error.code === '23505') {
        alert('A tag with this name already exists.');
      } else {
        alert('Failed to create tag.');
      }
    } finally {
      setIsAddingTag(false);
    }
  }

  function generateSlug(title: string) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function handleTitleChange(title: string) {
    setPost(prev => ({
      ...prev,
      title,
      slug: !id ? generateSlug(title) : prev.slug // Only auto-generate slug for new posts
    }));
  }

  async function handleSave(status: 'draft' | 'published' = 'draft') {
    if (!post.title || !post.content) {
      alert('Please provide a title and content');
      return;
    }

    setSaving(true);
    try {
      const { category_id, ...postWithoutCategoryId } = post;
      const postData = {
        ...postWithoutCategoryId,
        status,
        tags: selectedTags as any,
        categories: category_id ? [category_id] : []
      };

      if (id) {
        await updateBlogPost(id, postData);
        setSaveSuccess(status === 'published' ? 'Post published successfully!' : 'Draft saved successfully!');
        setTimeout(() => setSaveSuccess(null), 3000);
      } else {
        const newPost = await createBlogPost(postData);
        if (newPost) {
          setSaveSuccess(status === 'published' ? 'Post published successfully!' : 'Draft saved successfully!');
          setTimeout(() => {
            navigate(`/dashboard/blog/edit/${newPost.id}`);
          }, 1000);
        }
      }
    } catch (error: any) {
      console.error('Error saving post:', error);
      
      // Check for specific error types
      if (error.code === '23505' && error.message?.includes('slug')) {
        alert('A post with this title already exists. Please use a different title or edit the existing post.');
      } else if (error.message?.includes('slug')) {
        alert('A post with this URL already exists. Please change the title or slug.');
      } else {
        alert('Failed to save post. Please check the console for details.');
      }
    } finally {
      setSaving(false);
    }
  }

  // Image and crop handlers
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Create image URL for cropping
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setCropImageSrc(reader.result?.toString() || '');
      setShowCropModal(true);
    });
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 16 / 9)); // 16:9 aspect ratio for featured images
  }, []);

  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      alert('Please select a crop area');
      return;
    }

    try {
      setProcessing(true);

      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      // Create file from blob
      const croppedFile = new File([croppedBlob], 'featured-image.png', {
        type: 'image/png',
      });

      // Upload to Supabase Storage
      setUploadingImage(true);
      const fileExt = 'png';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `blog-images/${fileName}`;
      
      const { error } = await supabase.storage
        .from('blog-images')
        .upload(filePath, croppedFile, {
          cacheControl: '3600',
          upsert: false
        });
        
      if (error) throw error;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      // Update post with featured image URL
      setPost(prev => ({ ...prev, featured_image: publicUrl }));
      setPreviewImageUrl(publicUrl);

      // Close crop modal
      setShowCropModal(false);
      setCropImageSrc('');
    } catch (err) {
      console.error('Failed to crop and upload image:', err);
      alert('Failed to process image');
    } finally {
      setProcessing(false);
      setUploadingImage(false);
    }
  }, [completedCrop]);

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImageSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setPost(prev => ({ ...prev, featured_image: null }));
    setPreviewImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle content image upload for MDEditor
  const handleContentImageUpload = async (file: File): Promise<string> => {
    if (!file) throw new Error('No file provided');

    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('Please select a valid image file');
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image size must be less than 5MB');
    }

    setUploadingContentImage(true);
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `blog-images/${fileName}`;

      // Upload to Supabase storage
      const { error } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } finally {
      setUploadingContentImage(false);
    }
  };

  // Custom MDEditor command for image upload
  const imageUploadCommand: ICommand = {
    name: 'image-upload',
    keyCommand: 'image-upload',
    buttonProps: {
      'aria-label': 'Upload image',
      title: 'Upload image'
    },
    icon: (
      <span style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}>
        {uploadingContentImage ? (
          <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
        ) : (
          <ImagePlus style={{ width: '16px', height: '16px' }} />
        )}
      </span>
    ),
    execute: (state, api) => {
      if (uploadingContentImage) return;
      // Store the API instance for use in the upload handler
      (imageUploadInputRef.current as any)._mdEditorApi = api;
      imageUploadInputRef.current?.click();
    }
  };

  if (!isAdmin || loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Success Notifications */}
      <AnimatePresence>
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3 shadow-lg z-50"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">{saveSuccess}</span>
          </motion.div>
        )}
        {imageUploadSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3 shadow-lg z-50"
          >
            <ImagePlus className="w-5 h-5 text-blue-600" />
            <span className="text-blue-800 font-medium">{imageUploadSuccess}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden file input for content image upload */}
      <input
        ref={imageUploadInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={async (event) => {
          const file = event.target.files?.[0];
          if (!file) return;

          try {
            const imageUrl = await handleContentImageUpload(file);
            const imageMarkdown = `![Image description](${imageUrl})\n\n`;

            // Use MDEditor API to insert at cursor position if available
            const api = (imageUploadInputRef.current as any)?._mdEditorApi;
            if (api) {
              // Insert at cursor position using MDEditor API
              api.replaceSelection(imageMarkdown);
            } else {
              // Fallback: append to the end
              const currentContent = post.content || '';
              setPost(prev => ({
                ...prev,
                content: currentContent + imageMarkdown
              }));
            }

            // Show success message
            setImageUploadSuccess('Image uploaded successfully!');
            setTimeout(() => setImageUploadSuccess(null), 3000);

            // Clear the file input and stored API reference
            event.target.value = '';
            delete (imageUploadInputRef.current as any)?._mdEditorApi;
          } catch (error) {
            console.error('Error uploading image:', error);
            alert(error instanceof Error ? error.message : 'Failed to upload image');
          }
        }}
      />

      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard/blog')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Blog Management
        </button>
        
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h1>
          
          <div className="flex space-x-3">
            <button
              onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
              disabled={!id || !post.slug}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Eye className="w-5 h-5 mr-2" />
              Preview
            </button>
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5 mr-2" />
              Save Draft
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5 mr-2" />
              {post.status === 'published' ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={post.title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Enter post title"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL Slug
            </label>
            <div className="flex items-center">
              <span className="text-gray-500 mr-2">/blog/</span>
              <input
                type="text"
                value={post.slug || ''}
                onChange={(e) => setPost(prev => ({ ...prev, slug: e.target.value }))}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="url-friendly-slug"
              />
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Excerpt
            </label>
            <textarea
              value={post.excerpt || ''}
              onChange={(e) => setPost(prev => ({ ...prev, excerpt: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Brief description of the post"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <div data-color-mode="light">
              <MDEditor
                value={post.content || ''}
                onChange={(value) => setPost(prev => ({ ...prev, content: value || '' }))}
                preview="live"
                height={500}
                commands={[
                  // Add custom image upload command to the default commands
                  ...commands.getCommands(),
                  imageUploadCommand
                ]}
                textareaProps={{
                  placeholder: 'Write your blog post content here...'
                }}
                onPaste={async (event) => {
                  const items = event.clipboardData?.items;
                  if (!items) return;
                  
                  for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1) {
                      event.preventDefault();
                      const file = items[i].getAsFile();
                      if (!file) continue;
                      
                      try {
                        // Upload image
                        const fileExt = 'png'; // Default for pasted images
                        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
                        const filePath = `blog-images/${fileName}`;
                        
                        const { error } = await supabase.storage
                          .from('blog-images')
                          .upload(filePath, file);
                          
                        if (error) throw error;
                        
                        // Get public URL
                        const { data: { publicUrl } } = supabase.storage
                          .from('blog-images')
                          .getPublicUrl(filePath);
                          
                        // Insert markdown image at cursor position
                        const textarea = event.target as HTMLTextAreaElement;
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = post.content || '';
                        const imageMarkdown = `![Image](${publicUrl})`;
                        
                        const newContent = text.substring(0, start) + imageMarkdown + text.substring(end);
                        setPost(prev => ({ ...prev, content: newContent }));

                        // Show success message
                        setImageUploadSuccess('Image pasted and uploaded successfully!');
                        setTimeout(() => setImageUploadSuccess(null), 3000);
                      } catch (error) {
                        console.error('Error uploading pasted image:', error);
                        alert('Failed to upload pasted image. Please try again.');
                      }
                    }
                  }
                }}
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Use the toolbar for formatting or write in Markdown directly. Click the 📷 button to upload images, or paste images directly.
            </p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Featured Image */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Featured Image</h3>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              disabled={processing || uploadingImage}
            />
            
            {post.featured_image || previewImageUrl ? (
              <div className="relative">
                <img
                  src={previewImageUrl || post.featured_image || ''}
                  alt="Featured"
                  className="w-full rounded-lg object-cover aspect-video"
                />
                <button
                  onClick={handleRemoveImage}
                  disabled={processing || uploadingImage}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={processing || uploadingImage}
                  className="absolute bottom-2 right-2 px-3 py-1 bg-black/60 text-white rounded-lg hover:bg-black/70 transition-colors disabled:opacity-50 text-sm flex items-center space-x-1"
                >
                  <CropIcon className="w-4 h-4" />
                  <span>Change</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={processing || uploadingImage}
                className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors flex flex-col items-center justify-center disabled:opacity-50"
              >
                {uploadingImage ? (
                  <>
                    <Loader2 className="w-8 h-8 text-gray-400 mb-2 animate-spin" />
                    <span className="text-sm text-gray-600">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Upload Image</span>
                    <span className="text-xs text-gray-500 mt-1">16:9 ratio recommended</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Category */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Category</h3>
              <button
                onClick={() => setShowNewCategory(!showNewCategory)}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add new
              </button>
            </div>
            
            {showNewCategory ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="New category name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  onKeyDown={(e) => e.key === 'Enter' && createCategory()}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={createCategory}
                    disabled={isAddingCategory || !newCategoryName.trim()}
                    className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingCategory ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    onClick={() => {
                      setShowNewCategory(false);
                      setNewCategoryName('');
                    }}
                    className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <select
                value={(post as any).category_id || ''}
                onChange={(e) => setPost(prev => ({ ...prev, category_id: e.target.value || null }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tags */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
              <button
                onClick={() => setShowNewTag(!showNewTag)}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add new
              </button>
            </div>
            
            {showNewTag && (
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="New tag name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  onKeyDown={(e) => e.key === 'Enter' && createTag()}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={createTag}
                    disabled={isAddingTag || !newTagName.trim()}
                    className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingTag ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    onClick={() => {
                      setShowNewTag(false);
                      setNewTagName('');
                    }}
                    className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {tags.map(tag => (
                <label key={tag.id} className="flex items-center hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTags([...selectedTags, tag.id]);
                      } else {
                        setSelectedTags(selectedTags.filter(id => id !== tag.id));
                      }
                    }}
                    className="mr-2 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{tag.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* SEO Settings */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Settings</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Title
                  <span className="text-xs text-gray-500 ml-2">(Max 60 chars, used for search engines)</span>
                </label>
                <input
                  type="text"
                  value={post.meta_title || ''}
                  onChange={(e) => setPost(prev => ({ ...prev, meta_title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  placeholder="Custom SEO title (defaults to post title)"
                  maxLength={60}
                />
                <span className="text-xs text-gray-500">{(post.meta_title || '').length}/60</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                  <span className="text-xs text-gray-500 ml-2">(Max 155 chars, used for search engines)</span>
                </label>
                <textarea
                  value={post.meta_description || ''}
                  onChange={(e) => setPost(prev => ({ ...prev, meta_description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  placeholder="Custom SEO description (defaults to excerpt)"
                  maxLength={155}
                />
                <span className="text-xs text-gray-500">{(post.meta_description || '').length}/155</span>
              </div>

              {/* Open Graph Settings */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-md font-medium text-gray-800 mb-3">Social Media Preview (Open Graph)</h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Title
                      <span className="text-xs text-gray-500 ml-2">(Used for social media shares)</span>
                    </label>
                    <input
                      type="text"
                      value={post.og_title || ''}
                      onChange={(e) => setPost(prev => ({ ...prev, og_title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="Social media title (defaults to meta title or post title)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Description
                      <span className="text-xs text-gray-500 ml-2">(Used for social media shares)</span>
                    </label>
                    <textarea
                      value={post.og_description || ''}
                      onChange={(e) => setPost(prev => ({ ...prev, og_description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="Social media description (defaults to meta description or excerpt)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Image URL
                      <span className="text-xs text-gray-500 ml-2">(Used for social media preview image)</span>
                    </label>
                    <input
                      type="text"
                      value={post.og_image || ''}
                      onChange={(e) => setPost(prev => ({ ...prev, og_image: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="https://example.com/image.jpg (defaults to featured image)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Tip: Leave empty to use featured image. Recommended size: 1200x630px
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      <AnimatePresence>
        {showCropModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-4xl mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center space-x-2">
                  <CropIcon className="w-5 h-5" />
                  <span>Crop Featured Image</span>
                </h3>
                <button
                  onClick={handleCropCancel}
                  disabled={processing}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <ImageIcon className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-900">Crop Instructions</span>
                  </div>
                  <ul className="text-blue-800 space-y-1 text-xs">
                    <li>• Drag the corners to adjust the crop area</li>
                    <li>• Use a 16:9 aspect ratio for best results</li>
                    <li>• The cropped image will be optimized for blog display</li>
                  </ul>
                </div>

                {cropImageSrc && (
                  <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50 p-4">
                    <ReactCrop
                      crop={crop}
                      onChange={(_, percentCrop) => setCrop(percentCrop)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={16 / 9}
                      minWidth={400}
                      minHeight={225}
                      className="max-h-[40vh] sm:max-h-[50vh]"
                    >
                      <img
                        ref={imgRef}
                        alt="Crop preview"
                        src={cropImageSrc}
                        onLoad={onImageLoad}
                        className="max-h-[40vh] sm:max-h-[50vh] object-contain"
                      />
                    </ReactCrop>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCropCancel}
                    disabled={processing}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropComplete}
                    disabled={processing || !completedCrop}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-h-[44px]"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Apply Crop</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}